import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import {track} from '@/lib/telemetry'
import type {Evidence} from '@/hooks/sgsst/evidencias/use-evidence'
import type {Page} from '@/hooks/empresa/organizacion/use-businesses'
import type {SortDir} from '@/hooks/common/use-table-state'
import {NOTAS_PAGE_SIZE} from '@/hooks/sgsst/evaluacion/use-item-notes'
import type {components} from '@/lib/api-schema'

export type SgsstAction = components['schemas']['SgsstActionRead']
export type SgsstActionStatus = components['schemas']['SgsstActionStatus']
export type ActionNote = components['schemas']['ActionNoteRead']
export type MejoraEditada = components['schemas']['MejoraConfigurable']

const PLAN_ACTIONS_PAGE_SIZE = 20

export type ActionInput = {
  standard_code?: string | null
  description: string
  responsible_employee_id: string
  resources?: string | null
  target_date: string
  status?: SgsstActionStatus
}

export type ActionUpdateInput = {
  description?: string
  responsible_employee_id?: string
  resources?: string | null
  target_date?: string
  status?: SgsstActionStatus
  soporte_evidence_id?: string | null
}

// 100 es el tope del backend: la vista agrupada (Res. 0312/2019 art. 28) necesita el año
// entero para sus cálculos; la página compara `total` con `items.length` y lo avisa.
export function useActions(businessId: string | undefined, year?: number) {
  return useQuery({
    queryKey: ['actions', businessId, year ?? 'current'],
    queryFn: async () =>
      (
        await api.get<Page<SgsstAction>>(`/businesses/${businessId}/sgsst/actions`, {
          params: {page_size: 100, ...(year ? {year} : {})},
        })
      ).data,
    enabled: !!businessId,
  })
}

// Solo los ids que casan con la búsqueda del backend: los cálculos del grupo siguen sobre el
// conjunto completo, para que buscar no haga ver un grupo más terminado.
export function useActionsSearchIds(businessId: string | undefined, year: number | undefined, search: string) {
  return useQuery({
    queryKey: ['actions', businessId, year ?? 'current', 'search-ids', search],
    queryFn: async () => {
      const {data} = await api.get<Page<SgsstAction>>(`/businesses/${businessId}/sgsst/actions`, {
        params: {page_size: 100, search, ...(year ? {year} : {})},
      })
      return new Set(data.items.map((a) => a.id))
    },
    enabled: !!businessId && search.length > 0,
  })
}

export type PlanResumen = components['schemas']['PlanResumenRead']
export type FuentePlan = components['schemas']['FuentePlan']

// Declarado aquí para que la gráfica no dependa del orden del arreglo del backend.
export const FUENTES_PLAN: FuentePlan[] = [
  'matriz_riesgos',
  'matriz_legal',
  'investigacion',
  'autoevaluacion',
  'transversal',
]

/** Agregado en el servidor: `useActions` trae una sola página y desde 100 acciones el conteo
 * saldría corto. */
export function usePlanResumen(businessId: string | undefined, year?: number) {
  return useQuery({
    queryKey: ['actions', businessId, year ?? 'current', 'resumen'],
    queryFn: async () =>
      (
        await api.get<PlanResumen>(`/businesses/${businessId}/sgsst/plan/resumen`, {
          params: year ? {year} : {},
        })
      ).data,
    enabled: !!businessId,
  })
}

export type ActionsFlatParams = {
  page: number
  search: string
  sortBy: 'target_date' | 'status'
  sortDir: SortDir
  onlyPendientes: boolean
  // Se resuelven en el servidor: un filtro cliente sobre una página mostraría menos acciones
  // de las que la barra prometió.
  fuente: FuentePlan | null
  mes: number | null
}

export function useActionsFlat(businessId: string | undefined, year: number | undefined, params: ActionsFlatParams) {
  return useQuery({
    queryKey: ['actions', businessId, year ?? 'current', 'flat', params],
    queryFn: async () =>
      (
        await api.get<Page<SgsstAction>>(`/businesses/${businessId}/sgsst/actions`, {
          params: {
            page: params.page,
            page_size: PLAN_ACTIONS_PAGE_SIZE,
            search: params.search || undefined,
            sort_by: params.sortBy,
            sort_dir: params.sortDir,
            only_pendientes: params.onlyPendientes,
            fuente: params.fuente ?? undefined,
            mes: params.mes ?? undefined,
            ...(year ? {year} : {}),
          },
        })
      ).data,
    enabled: !!businessId,
    placeholderData: (previous) => previous,
  })
}

function useActionInvalidation(businessId: string) {
  const queryClient = useQueryClient()
  // Plan mutations move the checklist too (plan counts, resolve gating).
  return () => {
    queryClient.invalidateQueries({queryKey: ['actions', businessId]})
    queryClient.invalidateQueries({queryKey: ['sgsst', businessId]})
  }
}

export function useCreateAction(businessId: string) {
  const invalidate = useActionInvalidation(businessId)
  return useMutation({
    mutationFn: async (input: ActionInput) => api.post(`/businesses/${businessId}/sgsst/actions`, input),
    onSuccess: (_data, variables) => {
      invalidate()
      track('action_created', {business_id: businessId, standard_code: variables.standard_code ?? 'transversal'})
    },
  })
}

export function useUpdateAction(businessId: string) {
  const invalidate = useActionInvalidation(businessId)
  return useMutation({
    mutationFn: async ({id, ...fields}: ActionUpdateInput & {id: string}) =>
      api.patch(`/businesses/${businessId}/sgsst/actions/${id}`, fields),
    onSuccess: invalidate,
  })
}

export function useDeleteAction(businessId: string) {
  const invalidate = useActionInvalidation(businessId)
  return useMutation({
    mutationFn: async (actionId: string) => api.delete(`/businesses/${businessId}/sgsst/actions/${actionId}`),
    onSuccess: invalidate,
  })
}

export function useActionNotes(businessId: string | undefined, actionId: string | undefined, page = 1) {
  return useQuery({
    queryKey: ['action-notes', businessId, actionId, page],
    queryFn: async () =>
      (
        await api.get<Page<ActionNote>>(`/businesses/${businessId}/sgsst/actions/${actionId}/notas`, {
          params: {page, page_size: NOTAS_PAGE_SIZE},
        })
      ).data,
    enabled: !!businessId && !!actionId,
    placeholderData: (previous) => previous,
  })
}

export function useAddActionNote(businessId: string, actionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (text: string) =>
      (await api.post<ActionNote>(`/businesses/${businessId}/sgsst/actions/${actionId}/notas`, {text})).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['action-notes', businessId, actionId]})
      track('action_note_added', {business_id: businessId})
    },
  })
}

export function useDeleteActionNote(businessId: string, actionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (noteId: string) =>
      api.delete(`/businesses/${businessId}/sgsst/actions/${actionId}/notas/${noteId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['action-notes', businessId, actionId]})
      track('action_note_removed', {business_id: businessId})
    },
  })
}

export type EficaciaVeredicto = NonNullable<SgsstAction['eficacia']>

export type EficaciaInput = {
  veredicto: EficaciaVeredicto
  nota?: string | null
  soporte?: {
    object_key: string
    filename: string
    content_type: string
    size_bytes: number
  } | null
}

// Verificar y respaldar son un solo acto: el archivo ya subió y aquí se registra con el veredicto.
export function useVerifyEficacia(businessId: string, actionId: string) {
  const invalidate = useActionInvalidation(businessId)
  return useMutation({
    mutationFn: async (input: EficaciaInput) =>
      (await api.post<SgsstAction>(`/businesses/${businessId}/sgsst/actions/${actionId}/eficacia`, input)).data,
    onSuccess: (_data, variables) => {
      invalidate()
      track('action_eficacia_verified', {business_id: businessId, veredicto: variables.veredicto})
    },
  })
}

export type EficaciaCorreccionInput = Omit<EficaciaInput, 'veredicto'>

/** Otra petición a propósito: el veredicto no cabe en el cuerpo y no puede cambiar por aquí. */
export function useCorregirEficacia(businessId: string, actionId: string) {
  const invalidate = useActionInvalidation(businessId)
  return useMutation({
    mutationFn: async (input: EficaciaCorreccionInput) =>
      (await api.patch<SgsstAction>(`/businesses/${businessId}/sgsst/actions/${actionId}/eficacia`, input)).data,
    onSuccess: () => {
      invalidate()
      track('action_eficacia_corrected', {business_id: businessId})
    },
  })
}

export function useCreateSeguimiento(businessId: string, actionId: string) {
  const invalidate = useActionInvalidation(businessId)
  return useMutation({
    mutationFn: async (editado?: MejoraEditada) =>
      (await api.post<SgsstAction>(`/businesses/${businessId}/sgsst/actions/${actionId}/seguimiento`, editado ?? {}))
        .data,
    onSuccess: () => {
      invalidate()
      track('action_seguimiento_created', {business_id: businessId})
    },
  })
}

export function useActionEvidence(businessId: string | undefined, actionId: string | undefined, page = 1) {
  return useQuery({
    queryKey: ['action-evidence', businessId, actionId, page],
    queryFn: async () =>
      (
        await api.get<Page<Evidence>>(`/businesses/${businessId}/sgsst/actions/${actionId}/evidencia`, {
          params: {page, page_size: NOTAS_PAGE_SIZE},
        })
      ).data,
    enabled: !!businessId && !!actionId,
    placeholderData: (previous) => previous,
  })
}

export function useAttachActionEvidence(businessId: string, actionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      object_key: string
      filename: string
      content_type: string
      size_bytes: number
      validate_ai: boolean
    }) => (await api.post<Evidence[]>(`/businesses/${businessId}/sgsst/actions/${actionId}/evidencia`, input)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['action-evidence', businessId, actionId]})
      queryClient.invalidateQueries({queryKey: ['evidence', businessId]})
      track('action_evidence_uploaded', {business_id: businessId})
    },
  })
}
